import React, { use, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  InputGroup,
  InputGroupText,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import Breadcrumb from "../Common/Breadcrumbs";
import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { CUSTOMER_RETURNS_PAGE } from "../../routes/routeConstants";
import { debounce, initial, set } from "lodash";

import { formatPrice } from "../../helpers/utility";
import { productActions } from "../../sagas/productSlice";
import { FaBarcode, FaBox, FaBoxes, FaTag, FaTrash, FaUndo } from "react-icons/fa";
import Swal from "sweetalert2";
import { retourFournisseurActions } from "../../sagas/retourFournisseurSlice";
import { supplierActions } from "../../sagas/supplierSlice";
import { useSupplierFetch } from "../../hooks/supplierFetchHook";
import { useEntrepotFetch } from "../../hooks/entrepotFetchHook";
import { invoiceSupplierActions } from "../../sagas/invoiceSupplierSlice";
import { receiptNoteActions } from "../../sagas/receiptNoteSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";

const FournisseurRetourForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { list } = useSelector((state) => state.deliveryNotes);
  const { clients } = useSelector((state) => state.clients);
  const { invoices } = useSelector((state) => state.invoiceSupplier);
  const [documentType, setDocumentType] = useState("NONE");
  const [chooseProductModal, setChooseProductModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const unites = useSelector(status => status.products.unites);
  const { suppliers, fournisseurOptions, suppliersLoading } = useSupplierFetch();
  const { entrepotOptions, entrepotsLoading } = useEntrepotFetch();
  const { receiptNotes, isLoading } = useSelector((state) => state.receiptNote);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFournisseurId, setSelectedFournisseurId] = useState(null);
  const { newReturnNumber, loading } = useSelector((state) => state.retourFournisseur);
  const { transporteurs } = useSelector(state => state.transporteurs);

  const location = useLocation();
  const retour = location.state?.returnItem || null; // For legacy routes
  const isEditing = Boolean(retour);
  const canEdit = retour && retour.statut === 'BROUILLON' || !retour;
  
  const toggleChooseProductModal = () => setChooseProductModal(!chooseProductModal);
  const {
    products,
    loading: productLoading,
    totalPages,
    totalElements,
    pageSize,
  } = useSelector((status) => status.products);


  useEffect(() => {
    // Load clients and products
    dispatch(supplierActions.search({
      query: ""
    }));

    if (isEditing) {
      dispatch(retourFournisseurActions.getById({ id: retour.id }));
    }

  }, [dispatch, id, isEditing]);

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(retourFournisseurActions.generateNumero());
  }, []);

  useEffect(() => {
    dispatch(productActions.findAll({
      search: searchTerm,
      page: 0,
      size: pageSize
    }));

  }, [chooseProductModal]);



  const handleSupplierChange = (supplierId, setFieldValue) => {
    setSelectedFournisseurId(supplierId.value);
    setFieldValue("fournisseurId", supplierId.value);
    setSelectedItems([]);
    setDocumentType("NONE");
    setFieldValue("documentType", "NONE");
  };

  // Handle modal search input change
  const handleModalSearchChange = (e) => {
    const value = e.target.value;
    setModalSearchTerm(value);
    debouncedModalSearch(value);
  };

  const debouncedModalSearch = useCallback(
    debounce((query) => {
      setIsSearching(true);
      dispatch(productActions.findAll({
        search: query,
        page: 0,
        size: pageSize,
        onSuccess: () => setIsSearching(false)
      }));
      setIsSearching(false);
    }, 1000),
    [modalSearchTerm]
  );

  const validationSchema = Yup.object().shape({
    dateOperation: Yup.date().required(t("customerReturns.form.validation.dateRequired")),
    fournisseurId: Yup.number().required(t('reception.supplierMissing', 'Supplier is required')),
    entrepotId: Yup.number().required(t('reception.entrepotMissing')),
    documentType: Yup.string().required(t("customerReturns.form.validation.documentTypeRequired")),
    lignes: Yup.array().min(1, t("customerReturns.form.validation.itemsRequired")),
  });


  const round2 = (n) =>
    Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const toNumber = (v) =>
    Number.isFinite(Number(v)) ? Number(v) : 0;

  const calculateTotals = (lines = []) => {
    console.log(lines)
    let totalTTC = 0;
    let totalTVA = 0;

    lines.forEach((line) => {
      const qte = toNumber(line.quantiteRetournee);
      const lineTTC = round2(qte * line.prixUnitaireTTC);
      totalTTC = round2(totalTTC + lineTTC);
    });

    return {
      montantTTC: totalTTC,
    };
  };


  const handleSubmit = (values, { setSubmitting }) => {

    console.log(values)
    const totals = calculateTotals(values.lignes);
    const dataToSubmit = {
      ...values,
      ...totals,
    };

    if (isEditing) {


      dispatch(
        retourFournisseurActions.update({
          id: retour?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('customerReturns.messages.updateSuccess'),
            });
            navigate('/purchases-module/returns');
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: t('customerReturns.messages.saveError'),
            });
            setSubmitting(false);
          },
        })
      );
    } else {
      dispatch(
        retourFournisseurActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('customerReturns.messages.updateSuccess'),
            });
            navigate('/purchases-module/returns');
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: t('customerReturns.messages.saveError'),
            });
            setSubmitting(false);
          },
        })
      );
    }
  };

  // Determine initial document type
  const getInitialDocumentType = () => {
    if (isEditing && retour) {
      if (retour.factureOrigineId) return "FACTURE";
      if (retour.brOrigineId) return "BL";
    }
    return "NONE";
  };

  const initialValues = isEditing && retour
    ? {
      numeroRetour: retour.numeroRetour || "",
      dateOperation: retour.dateOperation || new Date().toISOString().split("T")[0],
      dateRetour: retour.dateRetour || new Date().toISOString().split("T")[0],
      fournisseurId: retour.fournisseurId || null,
      entrepotId: retour.entrepotId || null,
      documentType: getInitialDocumentType(),
            modeRemboursement: retour.modeRemboursement || "AVOIR",

      statut: retour.statut,
      lignes: retour.lignes?.map(line => ({
        articleId: line.articleId,
        articleNom: line.articleNom,
        articleCode: line.articleCode,
        quantiteRetournee: line.quantiteRetournee,
        prixUnitaireHT: line.prixUnitaireHT,
        prixUnitaireTTC: line.prixUnitaireTTC,
        tauxTVA: line.tauxTVA,
        unite: unites.find(u => u.code === line.unite)?.libelle,
        montantHT: line.montantHT,
        montantTVA: line.montantTVA,
        montantTTC: line.montantTTC
      })) || [],
    }
    : {
      numeroRetour: newReturnNumber,
      dateOperation: new Date().toISOString().split("T")[0],
      dateRetour: new Date().toISOString().split("T")[0],
      fournisseurId: null,
      entrepotId: null,
      documentType: "NONE",
            modeRemboursement: "AVOIR",

      lignes: [],
    };


  if ((loading && isEditing)) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                {isEditing ? `${t('retourFournisseur.edit')} ${retour.numeroRetour}` : t('retourFournisseur.new')}
              </h4>

            </CardBody>
          </Card>
        </Col>
      </Row>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>

            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <Row>
                      <Col md={3}>
                        <div className="mb-3">
                          <label className="form-label">
                            {t("customerReturns.form.fields.returnNumber")}
                          </label>
                          <Field
                            name="numeroRetour"
                            type="text"
                            className="form-control"
                            placeholder={t("customerReturns.form.placeholders.returnNumber")}
                            disabled={!canEdit}
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-3">
                          <label className="form-label">
                            {t("retourFournisseur.dateRetour")}
                          </label>
                          <Field
                            name="dateRetour"
                            type="date"
                            className={`form-control ${errors.dateRetour && touched.dateRetour ? "is-invalid" : ""
                              }`}
                            disabled={!canEdit}
                          />
                          <ErrorMessage
                            name="dateRetour"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </Col>
                      
                      {/* <Col md={3}>
                        <div className="mb-3">
                          <label className="form-label">
                            {t("customerReturns.form.fields.refundMode")}
                          </label>
                          <Field
                            name="modeRemboursement"
                            as="select"
                            className="form-select"
                          >
                            <option value="AVOIR">
                              {t("customerReturns.avoir")}
                            </option>
                            <option value="REMBOURSEMENT">
                              {t("customerReturns.remboursement")}
                            </option>
                            <option value="ECHANGE">
                              {t("customerReturns.echange")}
                            </option>
                          </Field>
                        </div>
                      </Col> */}
                      <Col md="3">
                        <Label htmlFor="entrepotId">{t('commande.entrepot')}:</Label>
                        <Select
                          id="entrepotId"
                          options={entrepotOptions}
                          value={entrepotOptions.find(option => option.value === values.entrepotId)}
                          onChange={(selectedOption) => setFieldValue('entrepotId', selectedOption.value)}
                          placeholder={t('commande.selectEntrepot')}
                          isSearchable
                          isDisabled={!canEdit}
                        />
                        {errors.entrepotId && touched.entrepotId && (
                          <div className="text-danger small mt-1">{errors.entrepotId}</div>
                        )}
                      </Col>
                      <Col md="3">
                        <Label htmlFor="supplier">{t('purchase.supplier')}:</Label>
                        <Select
                          id="supplier"
                          name="supplier"
                          isClearable
                          placeholder={t('purchase.selectSupplier')}
                          options={fournisseurOptions}
                          onChange={e => handleSupplierChange(e, setFieldValue)}
                          value={fournisseurOptions.find(option => option.value === values.fournisseurId)}
                          className="react-select"
                          classNamePrefix="select"
                          noOptionsMessage={() => t('purchase.noSupplierFound')}
                          loadingMessage={() => t('common.loading')}
                          isDisabled={!canEdit}
                        />
                        {touched.fournisseurId && errors.fournisseurId && (
                          <div className="invalid-feedback d-block">
                            {errors.fournisseurId}
                          </div>
                        )}
                      </Col>
                      
                    </Row>

                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col lg={12}>
                <Card className="shadow-sm">
                  <CardBody>

                    {selectedItems.length > 0 && (
                      <div className="mb-4">
                        <h5>{t("customerReturns.form.invoiceItems")}</h5>
                        <div className="table-responsive">
                          <table className="table table-hover table-striped table-bordered">
                            <thead className="thead-light">
                              <tr>
                                <th>{t("customerReturns.form.columns.item")}</th>
                                <th>{t("customerReturns.form.columns.quantity")}</th>
                                <th>{t("customerReturns.form.columns.unite")}</th>
                                <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                <th>{t("customerReturns.form.columns.total")}</th>
                                <th>{t("customerReturns.form.columns.actions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedItems.map((item, index) => (
                                <tr key={index}>
                                  <td>{item.articleNom}</td>
                                  <td>{item.quantite ?? item.quantiteLivree}</td>
                                  <td>{unites.find(u => u.code === item.unite)?.libelle}</td>
                                  <td>{formatPrice(item.prixUnitaireTTC)}</td>
                                  <td>{formatPrice(item.montantTTC)}</td>
                                  <td>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => {
                                        // Check if the item is already in the return lines
                                        const isItemAlreadyAdded = values.lignes.some(
                                          line => line.article === item.articleId || line.articleId === item.articleId
                                        );

                                      

                                        if (isItemAlreadyAdded) {
                                          // Show a warning toast
                                          toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
                                        } else {
                                          // Add the item to return lines
                                          const newLine = {
                                            articleId: item.articleId,
                                            article: item.articleId, // Keep both for compatibility
                                            articleLibelle: item.articleNom,
                                            quantiteRetournee: 1,
                                            prixUnitaireHT: item.prixUnitaireHT,
                                            prixUnitaireTTC: item.prixUnitaireTTC,
                                            tauxTVA: item.tauxTVA,
                                            unite: item.unite,
                                          };
                                          setFieldValue("lignes", [...values.lignes, newLine]);
                                        }
                                      }}
                                      disabled={!canEdit}
                                    >
                                      <i className="bx bx-plus"></i>{" "}
                                      {values.lignes.some(
                                        line => line.article === item.articleId || line.articleId === item.articleId
                                      )
                                        ? t("customerReturns.form.buttons.alreadyAdded")
                                        : t("customerReturns.form.buttons.add")}
                                    </Button>

                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Manual item addition section for when no document is selected */}
                    {documentType === "NONE" && (
                      <div className="mb-4">
                        <Row>
                          <Col md={12}>
                            <div className="mb-3">
                              <Button
                                color="primary"
                                onClick={toggleChooseProductModal}
                                disabled={!canEdit}
                              >
                                <i className="bx bx-plus me-1"></i>
                                {t("customerReturns.selectProduct")}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                    <FieldArray name="lignes">
                      {({ remove, push }) => (
                        <div className="table-responsive">
                          <table className="table table-hover table-striped table-bordered">
                            <thead className="thead-light">
                              <tr>
                                <th>{t("customerReturns.form.columns.item")}</th>
                                <th>{t("customerReturns.form.columns.returnedQte")}</th>
                                <th>{t("customerReturns.form.columns.unite")}</th>
                                <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                <th>{t("customerReturns.form.columns.total")}</th>
                                <th>{t("customerReturns.form.columns.actions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {values.lignes.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    {t("customerReturns.form.noItems")}
                                  </td>
                                </tr>
                              ) : (
                                values.lignes.map((line, index) => {
                                  const lineTotal = line.quantiteRetournee * line.prixUnitaireTTC;
                                  return (
                                    <tr key={index}>
                                      <td>
                                        {line.articleLibelle || "Article inconnu"}
                                      </td>
                                      <td>
                                        <Field
                                          name={`lignes.${index}.quantiteRetournee`}
                                          type="number"
                                          onFocus={(e) => e.target.select()} // Select the field content on focus
                                          min="1"
                                          step="1"
                                          className="form-control"
                                          style={{ width: "80px" }}
                                          disabled={!canEdit}
                                        />
                                      </td>

                                      <td>
                                        {unites.find(u => u.code === line.unite)?.libelle || line.unite}
                                      </td>
                                      <td>
                                        <InputGroup size="sm">
                                          <Field
                                            name={`lignes.${index}.prixUnitaireTTC`}
                                            type="number"
                                            onFocus={(e) => e.target.select()} // Select the field content on focus
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            style={{ width: "100px" }}
                                            disabled={!canEdit}
                                          />
                                          <InputGroupText>DH</InputGroupText>
                                        </InputGroup>
                                      </td>
                                      <td>{formatPrice(lineTotal)}</td>
                                      <td>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => remove(index)}
                                          disabled={!canEdit}
                                        >
                                          <FaTrash />
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                            <tfoot>
                              {values.lignes.length > 0 && (
                                <>
                                  <tr>
                                    <th colSpan="4" className="text-end">
                                      {t("customerReturns.form.totals.totalTTC")}:
                                    </th>
                                    <th>
                                      {formatPrice(
                                        calculateTotals(values.lignes).montantTTC
                                      )}
                                    </th>
                                    <th></th>
                                  </tr>
                                </>
                              )}
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </FieldArray>
                    {errors.lignes && typeof errors.lignes === "string" && (
                      <div className="text-danger mt-2">{errors.lignes}</div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    color="secondary"
                    onClick={() => navigate('/purchases-module/returns')}
                  >
                    {t("customerReturns.form.buttons.cancel")}
                  </Button>
                  <Button color="primary" type="submit" disabled={isSubmitting || !canEdit}>
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />{" "}
                        {t("customerReturns.form.buttons.saving")}
                      </>
                    ) : (
                      t("customerReturns.form.buttons.save")
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
            <Modal isOpen={chooseProductModal} toggle={toggleChooseProductModal} size="xl">
              <ModalHeader toggle={toggleChooseProductModal}>
                {t('product.chooseProduct')}
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md={12}>
                    <Input
                      type="text"
                      placeholder={t('product.searchPlaceholder')}
                      value={modalSearchTerm}
                      onChange={handleModalSearchChange}
                      className="me-2" // Added margin to the right
                    />
                  </Col>

                </Row>
                <>
                  <div className="cart-container" style={{
                    height: '300px',
                    overflowY: 'auto'
                  }}>

                    <Table hover bordered className="align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="align-middle">
                            <FaBarcode className="me-2 text-primary" size={12} />
                            {t('product.code')}
                          </th>
                          <th className="align-middle">
                            <FaBox className="me-2 text-primary" size={12} />
                            {t('product.description')}
                          </th>
                          <th className="align-middle text-end">
                            <FaTag className="me-2 text-primary" size={12} />
                            {t('product.salePrice')}
                          </th>
                          {/* <th className="align-middle text-end">
                                  <FaTag className="me-2 text-primary" size={12} />
                                  {t('product.buyingPrice')}
                              </th> */}
                          <th className="align-middle text-center">
                            <FaBoxes className="me-2 text-primary" size={12} />
                            {t('product.stockPhysique')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {!products || products.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <div className="text-muted">
                                {t('product.noProducts')}
                              </div>
                            </td>
                          </tr>
                        ) : (
                          products.map((product) => (
                            <tr
                              key={product.id}
                              onClick={() => {
                                // Check if the product is already in the return lines
                                const isProductAlreadyAdded = values.lignes.some(
                                  line => (line.article === product.id || line.articleId === product.id)
                                );

                                if (isProductAlreadyAdded) {
                                  // Show a warning toast
                                  toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
                                } else {
                                  // Add product to return lines
                                  const newLine = {
                                    article: product.id,
                                    articleId: product.id, // Keep both for compatibility
                                    articleLibelle: product.libelle,
                                    quantiteRetournee: 1,
                                    quantiteOriginale: 1,
                                    prixUnitaireHT: product.prixVenteHT || (product.prixVenteTTC1 / (1 + (product.tauxTVA || 0.2))),
                                    prixUnitaireTTC: product.prixVenteTTC1,
                                    tauxTVA: product.tauxTVA || 0.2,
                                    unite: unites.find(u => u.code === product.unite)?.libelle,
                                  };
                                  setFieldValue("lignes", [...values.lignes, newLine]);
                                  toggleChooseProductModal();
                                }
                              }}
                              style={{
                                cursor: 'pointer',
                                backgroundColor: values.lignes.some(
                                  line => (line.article === product.id || line.articleId === product.id)
                                ) ? '#f8f9fa' : 'inherit'
                              }}
                              className={values.lignes.some(
                                line => (line.article === product.id || line.articleId === product.id)
                              ) ? 'text-muted' : ''}
                            >
                              <td className="align-middle">{product.code}</td>
                              <td className="align-middle">
                                {product.libelle}
                                {values.lignes.some(
                                  line => (line.article === product.id || line.articleId === product.id)
                                ) && (
                                    <span className="ms-2 badge bg-light text-dark">
                                      {t("customerReturns.form.alreadyAdded")}
                                    </span>
                                  )}
                              </td>
                              <td className="align-middle text-end">
                                {formatPrice(product.prixVenteTTC1)}
                              </td>
                              <td className="align-middle text-center">{product.stockPhysique}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      {t('product.showing', {
                        start: (currentPage - 1) * pageSize + 1,
                        end: Math.min(currentPage * pageSize, totalElements),
                        total: totalElements,
                        currentPage: currentPage,
                        totalPages: totalPages
                      })}
                    </small>
                  </div>
                </>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => {
                  setModalSearchTerm('');
                  setCurrentPage(1);
                  toggleChooseProductModal()
                }}>
                  {t('common.cancel')}
                </Button>
              </ModalFooter>
            </Modal>
          </Form>
        )}
      </Formik>
    </Container>

  );
};

export default FournisseurRetourForm;


